import {formatISOByGuard, getDateString} from '@shared/helpers/date'
import {eq} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

import {initDetailByAPI, initRefundByAPI} from '@web/actions/detail'
import {DetailPaymentClass, RentalClass} from '@web/core/constants'
import {PAY_M} from '@web/core/urls'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {getPatternedRegExp} from '@web/helpers/policies'
import {getRefundInitialState, getCardInterestType, parseBankCode} from '@web/slices/admissionDetail/slice'
import makeSlice from '@web/slices/admissionDetail/sliceMaker'

const admissionDetailSlice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initDetailByAPI.fulfilled, (state, {payload}) => {
                const {isMobileDisplay} = payload
                const {order, pay} = payload.data

                const {subscription, familyPayOrder} = order

                state.id = order.orderNo
                state.date = new Date(order.orderDateTime).toISOString()

                state.pay.failReason = order.payFailReason
                state.pay.pendingPayment = {
                    isPendingPayment: order.pendingPayment,
                    isFailedPendingPayment: order.failedPendingPayment,
                }
                state.usedPaymentMethod.familyPay = familyPayOrder
                    ? {
                          isPayer: familyPayOrder.familyPayer,
                          requesterName: familyPayOrder.payRequesterName,
                          requestMemo: decode(familyPayOrder.familyPayRequestMemo),
                          expirationDate: getDateString({
                              date: familyPayOrder.payExpirationDate,
                              format: '-',
                          }),
                          payerPayMemberNo: familyPayOrder.payerPayMemberNo,
                          profileImageUrl: familyPayOrder.profileImageUrl,
                      }
                    : undefined

                state.pay.orderRepurchaseAvailable = order.orderRepurchaseAvailable
                state.usedPaymentMethod.subscription = subscription
                    ? {
                          scheduleId: subscription.scheduleId,
                          round: subscription.subscriptionRound,
                          id: subscription.subscriptionId ?? '',
                          status: subscription.subscriptionStatusType ?? 'NONE',
                          period: subscription.period ?? '',
                          totalRound: subscription.totalRoundNo ?? 0,
                          registerDateTime: formatISOByGuard(subscription.subscriptionRegisterDateTime) ?? '',
                          nextDeliveryDateTime: subscription.nextDeliveryDate,
                          nextPaymentDateTime: subscription.nextPaymentDate,
                          maxBenefitRatio: subscription.maxSubscriptionBenefitRatio,
                      }
                    : undefined

                state.usedPaymentMethod.rental = order.rental
                    ? {
                          contractNo: order.rental.contractNo,
                          orderClassType: order.rental.rentalOrderClassType,
                          round: order.rental.subscriptionRound,
                          managementText: order.rental.rentalManagementText,
                          /** 수기결제 Case는 선납금 여부를 아예 미노출해야하므로 undefined로 둔다
                           * isPrePay === true > '선납금 있음'
                           * isPrePay === false > '선납금 없음'
                           */
                          isPrePay: eq(order.rental.rentalOrderClassType, RentalClass.MANUAL_PAYMENT)
                              ? null
                              : (order.rental.rentalPrePay ?? null),
                      }
                    : undefined

                state.usedPaymentMethod.recharge = pay.rechargePointAdmission
                    ? {
                          rechargePointBankName: pay.rechargePointAdmission.rechargePointBankName ?? '',
                          maskedRechargePointAccountNo: pay.rechargePointAdmission.maskedRechargePointAccountNo ?? '',
                      }
                    : undefined

                state.pay.totalPayAmount = pay.totalInitPayAmount
                state.pay.rewardPointPayAmount = pay.rewardPointPayAmount
                state.pay.chargePointPayAmount = pay.chargePointPayAmount
                state.pay.rechargePointAmount = pay.rechargePointAmount
                state.pay.promotionApplyAmount = pay.promotionApplyAmount
                state.pay.payLaterAmount = pay.payLaterAmount
                state.pay.generalPayAmount = pay.generalPayAmount
                state.pay.generalPayMeansClassType = (function () {
                    if (pay.easyPay) {
                        if (pay.generalPayMeansClassType === DetailPaymentClass.BANK) {
                            return DetailPaymentClass.BANK_EASY_PAY
                        }
                        if (pay.generalPayMeansClassType === DetailPaymentClass.CREDIT_CARD) {
                            return DetailPaymentClass.CREDIT_CARD_EASY_PAY
                        }
                        if (pay.generalPayMeansClassType === DetailPaymentClass.MOBILE) {
                            return DetailPaymentClass.MOBILE_EASY_PAY
                        }
                    } else if (eq(pay.cardPayAdmission?.payAuthenticationType, DetailPaymentClass.SAMSUNG_PAY)) {
                        return DetailPaymentClass.SAMSUNG_PAY
                    } else if (eq(pay.cardPayAdmission?.payAuthenticationType, DetailPaymentClass.APPLE_PAY)) {
                        return DetailPaymentClass.APPLE_PAY
                    }

                    return pay.generalPayMeansClassType
                })()
                state.pay.isEasyPay = pay.easyPay

                const maskedCardRegExp = getPatternedRegExp({pattern: [[4], [4], [4], [3, 4]]})

                state.usedPaymentMethod.card = pay.cardPayAdmission
                    ? {
                          companyName: pay.cardPayAdmission.cardCompanyName ?? '',
                          maskedNumber: (pay.cardPayAdmission.maskedCardNo ?? '').replace(
                              maskedCardRegExp,
                              '$1 $2 $3 $4',
                          ),
                          installment: {
                              month: pay.cardPayAdmission.installmentMonth,
                              interestType: getCardInterestType({
                                  freeInterestInstallment: pay.cardPayAdmission.freeInterestInstallment,
                                  partFreeInterestInstallment: pay.cardPayAdmission.partFreeInterestInstallment,
                              }),
                          },
                      }
                    : undefined

                state.usedPaymentMethod.account = pay.bankPayAdmission
                    ? {
                          accountTransferBankName: pay.bankPayAdmission?.realTimeAccountTransferBankName ?? '',
                          maskedNumber: pay.bankPayAdmission?.maskedAccountNo ?? '',
                          pendingPayment: {
                              isPendingPayment: order.pendingPayment,
                              isFailedPendingPayment: order.failedPendingPayment,
                          },
                      }
                    : undefined

                state.usedPaymentMethod.mobile = pay.mobilePayAdmission
                    ? {
                          maskedCellPhoneNumber: pay.mobilePayAdmission.maskedCellPhoneNumber ?? '',
                      }
                    : undefined

                if (pay.virtualAccountPayAdmission) {
                    /** 서버에서는 BNK001로 내려주는데, 스토어에서는 001로 관리 */
                    const parsedVirtualBankCode = parseBankCode(
                        pay.virtualAccountPayAdmission.virtualAccountBankCodeType ?? '',
                        'remove',
                    )

                    state.usedPaymentMethod.virtualAccount = {
                        virtualAccountBankName: pay.virtualAccountPayAdmission.virtualAccountBankName ?? '',
                        ...(parsedVirtualBankCode && {
                            virtualAccountBankCode: parsedVirtualBankCode,
                        }),
                        virtualAccountNo: pay.virtualAccountPayAdmission.virtualAccountNo ?? '',
                        virtualAccountHolderName: pay.virtualAccountPayAdmission.virtualAccountHolderName,
                        payExpirationDate: getDateString({
                            date: pay.virtualAccountPayAdmission.payExpirationDate,
                            format: '-',
                        }),
                    }
                }

                state.usedPaymentMethod.skip = pay.skipPayAdmission?.nextPayMeansClassType
                    ? {
                          nextPayMeansClassType: pay.skipPayAdmission.nextPayMeansClassType,
                      }
                    : undefined

                if (order.exposureReceipt) {
                    state.receipts = [
                        {
                            type: 'ETC',
                            url: qs.stringifyUrl({
                                url: `${PAY_M}/o/receipt/hist/${order.orderNo}`,
                                query: !isMobileDisplay
                                    ? {
                                          _callback: 'callbackParentReload',
                                          _entryPoint: 'orderDetail',

                                          targetOrigin: encodeURIComponent(window.location.href),
                                      }
                                    : {},
                            }),
                        },
                    ]
                }
            })
            .addCase(initRefundByAPI.fulfilled, (state, {payload}) => {
                if (payload.refundOccurred) {
                    state.refund = getRefundInitialState()
                    state.refund.noPayCanceled = payload.refundPayView.noPayCanceled
                    state.refund.amount = {
                        totalAmount: payload.totalRefundAmount,
                        deductedAmount: payload.refundDeductedAmount,
                        alternativeAmount: 0,
                        productAmount: payload.productRefundAmount,
                        authenticationType: eq(
                            payload.refundPayView?.payAuthenticationType,
                            DetailPaymentClass.SAMSUNG_PAY,
                        )
                            ? DetailPaymentClass.SAMSUNG_PAY
                            : eq(payload.refundPayView?.payAuthenticationType, DetailPaymentClass.APPLE_PAY)
                              ? DetailPaymentClass.APPLE_PAY
                              : DetailPaymentClass.NONE,
                        amountByPaymentMethod: {
                            generalPayAmount: 0,
                            generalPayMeansClassType: null,
                            rewardPointAmount: payload.refundPayView.rewardPointRefundAmount,
                            chargedPointAmount: payload.refundPayView.chargedPointRefundAmount,
                            chargeAmount: payload.refundPayView.chargeAmountRefundAmount,
                            cardPromotionPointAmount: payload.refundPayView.cardPromotionPointRefundAmount,
                            payLaterAmount: payload.refundPayView.payLaterRefundAmount,
                            cardAmount: payload.refundPayView.cardRefundAmount,
                            bankAmount: payload.refundPayView.bankRefundAmount,
                            virtualAccountAmount: payload.refundPayView.virtualAccountRefundAmount,
                            mobileAmount: payload.refundPayView.mobileRefundAmount,
                            familyPayAmount: payload.refundPayView.familyPayRefundAmount,
                            giftCardAmount: 0,
                        },
                        deposit: {
                            coolBag: payload.refundDepositView.coolBagDepositRefundAmount,
                            reusableBag: payload.refundDepositView.reusableBagDepositRefundAmount,
                            singleUseCup: payload.refundDepositView.singleUseCupDepositRefundAmount,
                            reusableCup: payload.refundDepositView.reusableCupDepositRefundAmount,
                            plasticBag: payload.refundDepositView.pickupBagDepositRefundAmount,
                        },
                    }
                }
            })
    },
})

export default admissionDetailSlice.reducer
